import React, { useState, useEffect, useRef } from 'react';
import { User, CompetitionPost } from '../types';
import { db, collection, getDocs, addDoc, updateDoc, deleteDoc, doc, query, orderBy, onSnapshot } from '../firebase';
import { 
  Bold, Italic, Underline, AlignLeft, AlignCenter, AlignRight, 
  List, ListOrdered, Image, Video, Plus, Trash2, ChevronLeft, ChevronRight,
  Eye, EyeOff, Sparkles, Heading1, Heading2, Quote, Palette, Check, HelpCircle, Maximize2, X, ExternalLink
} from 'lucide-react';

interface CompetitionsPageProps {
  isAdmin: boolean;
}

const CompetitionsPage: React.FC<CompetitionsPageProps> = ({ isAdmin }) => {
  const [posts, setPosts] = useState<CompetitionPost[]>([]);
  const [currentIndex, setCurrentIndex] = useState(0);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [editingPost, setEditingPost] = useState<CompetitionPost | null>(null);

  // Form State
  const [title, setTitle] = useState('');
  const [content, setContent] = useState('');
  const [priority, setPriority] = useState<number>(0); // 0: Normal, 1: 1st Rank, 2: 2nd Rank, 3: 3rd Rank
  const [imageUrl, setImageUrl] = useState('');
  const [videoUrl, setVideoUrl] = useState('');
  const [imageUrls, setImageUrls] = useState<string[]>(['']);
  const [videoUrls, setVideoUrls] = useState<string[]>(['']);
  const [imageTitles, setImageTitles] = useState<string[]>(['']);
  const [videoTitles, setVideoTitles] = useState<string[]>(['']);
  const [zoomMedia, setZoomMedia] = useState<{ type: string; url: string; title?: string } | null>(null);
  const [useSafePlayer, setUseSafePlayer] = useState(false);

  // Custom states for Rich Editor & Viewer Carousel
  const [activeMediaIndexes, setActiveMediaIndexes] = useState<Record<string, number>>({});
  const [isPreviewMode, setIsPreviewMode] = useState(false);
  const textareaRef = useRef<HTMLTextAreaElement>(null);

  useEffect(() => {
    const q = query(collection(db, "competitions"), orderBy("createdAt", "desc"));
    const unsubscribe = onSnapshot(q, (snapshot) => {
      const data: CompetitionPost[] = [];
      snapshot.forEach(doc => {
        data.push({ id: doc.id, ...doc.data() } as CompetitionPost);
      });
      // Sort client-side: priority 1, 2, 3 first, then normal (priority 0 or undefined)
      data.sort((a, b) => {
        const pA = a.priority && a.priority > 0 ? a.priority : 999;
        const pB = b.priority && b.priority > 0 ? b.priority : 999;
        if (pA !== pB) {
          return pA - pB;
        }
        return new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime();
      });
      setPosts(data);
    });
    return () => unsubscribe();
  }, []);

  const handleNext = () => {
    if (currentIndex + 3 < posts.length) {
      setCurrentIndex(prev => prev + 1);
    }
  };

  const handlePrev = () => {
    if (currentIndex > 0) {
      setCurrentIndex(prev => prev - 1);
    }
  };

  const openModal = (post?: CompetitionPost) => {
    if (post) {
      setEditingPost(post);
      setTitle(post.title);
      setContent(post.content);
      setPriority(post.priority || 0);
      
      // Load image array (or fall back to single string)
      const initialImages = post.imageUrls && post.imageUrls.length > 0 
        ? [...post.imageUrls] 
        : (post.imageUrl ? [post.imageUrl] : ['']);
      setImageUrls(initialImages);
      
      // Load video array (or fall back to single string)
      const initialVideos = post.videoUrls && post.videoUrls.length > 0 
        ? [...post.videoUrls] 
        : (post.videoUrl ? [post.videoUrl] : ['']);
      setVideoUrls(initialVideos);

      // Load image titles (keep length consistent with images)
      const initialImageTitles = post.imageTitles && post.imageTitles.length > 0
        ? [...post.imageTitles]
        : initialImages.map(() => '');
      while (initialImageTitles.length < initialImages.length) {
        initialImageTitles.push('');
      }
      setImageTitles(initialImageTitles);

      // Load video titles (keep length consistent with videos)
      const initialVideoTitles = post.videoTitles && post.videoTitles.length > 0
        ? [...post.videoTitles]
        : initialVideos.map(() => '');
      while (initialVideoTitles.length < initialVideos.length) {
        initialVideoTitles.push('');
      }
      setVideoTitles(initialVideoTitles);
      
      setImageUrl(post.imageUrl || '');
      setVideoUrl(post.videoUrl || '');
    } else {
      setEditingPost(null);
      setTitle('');
      setContent('');
      setPriority(0);
      setImageUrls(['']);
      setImageTitles(['']);
      setVideoUrls(['']);
      setVideoTitles(['']);
      setImageUrl('');
      setVideoUrl('');
    }
    setIsPreviewMode(false);
    setIsModalOpen(true);
  };

  const savePost = async () => {
    try {
      if (!title.trim()) {
        alert("제목을 입력해 주세요.");
        return;
      }
      if (!content.trim()) {
        alert("내용을 입력해 주세요.");
        return;
      }

      // Filter empty inputs and keep titles aligned
      const cleanedImages: string[] = [];
      const cleanedImageTitles: string[] = [];
      imageUrls.forEach((url, i) => {
        const trimmed = url.trim();
        if (trimmed) {
          cleanedImages.push(trimmed);
          cleanedImageTitles.push((imageTitles[i] || '').trim());
        }
      });

      const cleanedVideos: string[] = [];
      const cleanedVideoTitles: string[] = [];
      videoUrls.forEach((url, i) => {
        const trimmed = url.trim();
        if (trimmed) {
          cleanedVideos.push(trimmed);
          cleanedVideoTitles.push((videoTitles[i] || '').trim());
        }
      });

      const postData = {
        title: title.trim(),
        content: content.trim(),
        imageUrl: cleanedImages[0] || '',
        videoUrl: cleanedVideos[0] || '',
        imageUrls: cleanedImages,
        videoUrls: cleanedVideos,
        imageTitles: cleanedImageTitles,
        videoTitles: cleanedVideoTitles,
        priority: Number(priority) || 0,
        createdAt: editingPost ? editingPost.createdAt : new Date().toISOString()
      };

      if (editingPost) {
        await updateDoc(doc(db, "competitions", editingPost.id), postData);
      } else {
        await addDoc(collection(db, "competitions"), postData);
      }
      setIsModalOpen(false);
    } catch (error: any) {
      console.error("Error saving post:", error);
      alert(`저장 중 오류가 발생했습니다.\n상세 원인: ${error?.message || error}`);
    }
  };

  const deletePost = async (id: string) => {
    if (confirm('정말로 삭제하시겠습니까?')) {
      await deleteDoc(doc(db, "competitions", id));
      if (currentIndex > 0 && currentIndex >= posts.length - 1) {
        setCurrentIndex(prev => prev - 1);
      }
    }
  };

  // Multiple Image URL Input Handlers
  const handleAddImageUrlField = () => {
    setImageUrls([...imageUrls, '']);
    setImageTitles([...imageTitles, '']);
  };

  const handleRemoveImageUrlField = (index: number) => {
    const updatedUrls = imageUrls.filter((_, i) => i !== index);
    const updatedTitles = imageTitles.filter((_, i) => i !== index);
    setImageUrls(updatedUrls.length > 0 ? updatedUrls : ['']);
    setImageTitles(updatedTitles.length > 0 ? updatedTitles : ['']);
  };

  const handleImageUrlChange = (index: number, value: string) => {
    const updated = [...imageUrls];
    updated[index] = value;
    setImageUrls(updated);
  };

  const handleImageTitleChange = (index: number, value: string) => {
    const updated = [...imageTitles];
    updated[index] = value;
    setImageTitles(updated);
  };

  // Multiple Video URL Input Handlers
  const handleAddVideoUrlField = () => {
    setVideoUrls([...videoUrls, '']);
    setVideoTitles([...videoTitles, '']);
  };

  const handleRemoveVideoUrlField = (index: number) => {
    const updatedUrls = videoUrls.filter((_, i) => i !== index);
    const updatedTitles = videoTitles.filter((_, i) => i !== index);
    setVideoUrls(updatedUrls.length > 0 ? updatedUrls : ['']);
    setVideoTitles(updatedTitles.length > 0 ? updatedTitles : ['']);
  };

  const handleVideoUrlChange = (index: number, value: string) => {
    const updated = [...videoUrls];
    updated[index] = value;
    setVideoUrls(updated);
  };

  const handleVideoTitleChange = (index: number, value: string) => {
    const updated = [...videoTitles];
    updated[index] = value;
    setVideoTitles(updated);
  };

  // Rich Text Insertion Handler
  const applyFormat = (tagOpen: string, tagClose: string) => {
    const el = textareaRef.current;
    if (!el) return;
    const start = el.selectionStart;
    const end = el.selectionEnd;
    const text = el.value;
    const selected = text.substring(start, end);
    const replacement = tagOpen + (selected || '') + tagClose;
    const newValue = text.substring(0, start) + replacement + text.substring(end);
    setContent(newValue);
    
    setTimeout(() => {
      el.focus();
      el.setSelectionRange(start + tagOpen.length, start + tagOpen.length + (selected || '').length);
    }, 0);
  };

  const visiblePosts = posts.slice(currentIndex, currentIndex + 3);

  // Embed YouTube Video Helper
  const getYoutubeVideoId = (url: string): string | null => {
    if (!url) return null;
    const trimmedUrl = url.trim();
    let videoId = null;
    
    // 1. YouTube Shorts check
    if (trimmedUrl.includes('/shorts/')) {
      const parts = trimmedUrl.split('/shorts/');
      if (parts[1]) {
        videoId = parts[1].split(/[?#&]/)[0];
      }
    } 
    
    // 2. Regular watch/share/embed patterns
    if (!videoId) {
      const regExp = /^.*(youtu.be\/|v\/|u\/\w\/|embed\/|watch\?v=|&v=)([^#&?]*).*/;
      const match = trimmedUrl.match(regExp);
      if (match && match[2] && match[2].length === 11) {
        videoId = match[2];
      }
    }
    
    // 3. Fallback URL search params parsing
    if (!videoId) {
      try {
        const urlObj = new URL(trimmedUrl);
        if (urlObj.hostname.includes('youtube.com')) {
          videoId = urlObj.searchParams.get('v');
        } else if (urlObj.hostname.includes('youtu.be')) {
          videoId = urlObj.pathname.substring(1);
        }
      } catch (e) {
        // Safe skip
      }
    }
    
    return (videoId && videoId.length === 11) ? videoId : null;
  };

  const getEmbedUrl = (url: string, useSafe: boolean = false) => {
    const videoId = getYoutubeVideoId(url);
    if (!videoId) return null;
    
    const domain = useSafe ? 'www.youtube-nocookie.com' : 'www.youtube.com';
    return `https://${domain}/embed/${videoId}?autoplay=0&rel=0`;
  };

  const handleMediaNext = (postId: string, maxLen: number) => {
    setActiveMediaIndexes(prev => {
      const cur = prev[postId] || 0;
      return { ...prev, [postId]: (cur + 1) % maxLen };
    });
  };

  const handleMediaPrev = (postId: string, maxLen: number) => {
    setActiveMediaIndexes(prev => {
      const cur = prev[postId] || 0;
      return { ...prev, [postId]: (cur - 1 + maxLen) % maxLen };
    });
  };

  return (
    <div className="min-h-screen bg-gray-50 flex flex-col items-center justify-start p-4 pt-1 md:pt-2 pb-16 relative font-sans text-gray-800">
      
      {/* Background Decor */}
      <div className="fixed inset-0 pointer-events-none" style={{ backgroundImage: 'radial-gradient(#e5e7eb 2px, transparent 2px)', backgroundSize: '32px 32px' }}></div>
      <div className="absolute top-0 left-0 w-full h-1/2 bg-gradient-to-b from-yellow-50 to-transparent pointer-events-none"></div>
 
      <div className="max-w-5xl w-full relative z-10 flex flex-col items-center">
        
        {/* Header Title */}
        <div className="w-full relative overflow-hidden rounded-3xl mb-8 h-56 md:h-80 flex flex-col justify-center items-center text-center px-6 shadow-2xl border border-yellow-400/30">
          {/* Background Image with Cover - fixed with ?alt=media */}
          <div 
            className="absolute inset-0 bg-cover bg-center transition-transform duration-1000 hover:scale-105"
            style={{ 
              backgroundImage: "url('https://firebasestorage.googleapis.com/v0/b/makerstory-shopping.firebasestorage.app/o/gprc_top.png?alt=media')" 
            }}
          />
          {/* Smooth custom gradient overlay to integrate with the main theme and enhance readability */}
          <div className="absolute inset-0 bg-gradient-to-r from-black/85 via-black/40 to-transparent" />
          
          {/* Golden decorative ambient glow */}
          <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-72 h-72 bg-yellow-400/10 rounded-full blur-3xl pointer-events-none animate-pulse" />
 
          {/* Text Content */}
          <div className="relative z-10 flex flex-col items-center">
            {/* Hard, solid, angular blocky title in white and double the size */}
            <h1 className="text-5xl md:text-8xl font-black tracking-tighter text-white uppercase select-none drop-shadow-[0_4px_14px_rgba(0,0,0,0.95)]">
              G-PRC 2026
            </h1>
            <p className="mt-2.5 md:mt-3 text-[10px] md:text-xs font-bold text-yellow-300 tracking-[0.2em] md:tracking-[0.3em] uppercase drop-shadow-[0_2px_4px_rgba(0,0,0,0.9)] bg-black/40 px-3 py-1 rounded-full border border-yellow-400/10 backdrop-blur-sm">
              대회의 다채로운 소식과 챔피언십을 확인하세요!
            </p>
          </div>
        </div>

        {/* Controls (Navigator & Admin) */}
        <div className="w-full flex justify-between items-center mb-8 gap-4">
          <div>
            {isAdmin && (
              <button 
                onClick={() => openModal()}
                className="px-5 py-2.5 bg-indigo-600 text-white rounded-xl font-bold hover:bg-indigo-700 hover:scale-102 active:scale-95 transition shadow-lg flex items-center gap-2"
              >
                <Plus className="w-4 h-4" /> 새 게시물 작성
              </button>
            )}
          </div>
          
          <div className="flex items-center gap-3 bg-white p-2 rounded-2xl shadow-sm border border-gray-100">
            <button 
              onClick={handlePrev} 
              disabled={currentIndex === 0}
              className={`p-2 rounded-xl transition ${currentIndex === 0 ? 'text-gray-300 cursor-not-allowed' : 'text-gray-700 hover:bg-gray-100'}`}
              aria-label="Previous Post"
            >
              <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={3} stroke="currentColor" className="w-5 h-5">
                <path strokeLinecap="round" strokeLinejoin="round" d="M4.5 10.5L12 3m0 0l7.5 7.5M12 3v18" />
              </svg>
            </button>
            <div className="px-2 text-xs font-black text-gray-400">
              {posts.length > 0 ? `${currentIndex + 1} - ${Math.min(currentIndex + 3, posts.length)} / ${posts.length}` : '0 / 0'}
            </div>
            <button 
              onClick={handleNext} 
              disabled={currentIndex + 3 >= posts.length}
              className={`p-2 rounded-xl transition ${currentIndex + 3 >= posts.length ? 'text-gray-300 cursor-not-allowed' : 'text-gray-700 hover:bg-gray-100'}`}
              aria-label="Next Post"
            >
              <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={3} stroke="currentColor" className="w-5 h-5">
                <path strokeLinecap="round" strokeLinejoin="round" d="M19.5 13.5 12 21m0 0-7.5-7.5M12 21V3" />
              </svg>
            </button>
          </div>
        </div>

        {/* Posts Pinboard Layout */}
        <div className="w-full flex flex-col gap-8 md:gap-12">
          {posts.length === 0 ? (
            <div className="text-center py-20 text-gray-400 font-bold bg-white rounded-3xl border border-gray-100 shadow-sm w-full">등록된 소식이 없습니다.</div>
          ) : (
            visiblePosts.map((post, idx) => {
              // Combine legacy and array format for images/videos
              const images = post.imageUrls && post.imageUrls.length > 0 ? post.imageUrls : (post.imageUrl ? [post.imageUrl] : []);
              const videos = post.videoUrls && post.videoUrls.length > 0 ? post.videoUrls : (post.videoUrl ? [post.videoUrl] : []);
              
              const imageTitlesList = post.imageTitles && post.imageTitles.length > 0 ? post.imageTitles : [];
              const videoTitlesList = post.videoTitles && post.videoTitles.length > 0 ? post.videoTitles : [];

              const mediaList = [
                ...videos.map((url, vIdx) => ({ type: 'video', url, title: videoTitlesList[vIdx] || '' })),
                ...images.map((url, iIdx) => ({ type: 'image', url, title: imageTitlesList[iIdx] || '' }))
              ].filter(item => item.url);

              const activeIndex = activeMediaIndexes[post.id] || 0;
              const currentMedia = mediaList[activeIndex];

              return (
                <div 
                  key={post.id} 
                  className={`w-full bg-white rounded-3xl p-6 md:p-10 shadow-xl shadow-gray-200/50 relative transform transition-all duration-500 flex flex-col md:flex-row gap-8 ${idx % 2 === 0 ? 'rotate-[-1deg] md:rotate-[-0.5deg]' : 'rotate-[1deg] md:rotate-[0.5deg]'}`}
                >
                  {/* Pin Decor */}
                  <div className="absolute -top-4 left-1/2 -translate-x-1/2 md:left-8 w-8 h-8 bg-red-400 rounded-full shadow-inner shadow-red-600/50 border-[3px] border-white z-10 flex items-center justify-center">
                    <div className="w-2 h-2 bg-white/40 rounded-full blur-[1px]"></div>
                  </div>

                  {/* Media Section with Combined Slider */}
                  <div className="w-full md:w-1/2 rounded-2xl overflow-hidden bg-black flex-shrink-0 relative group border border-gray-800 flex flex-col items-center justify-center h-[350px] md:h-[450px] shadow-lg">
                    {mediaList.length === 0 ? (
                      <div className="w-full h-full flex flex-col items-center justify-center text-gray-400 bg-gray-900 font-bold gap-2">
                        <Image className="w-8 h-8 text-gray-600" />
                        <span>No Media Content</span>
                      </div>
                    ) : (
                      <div className="relative w-full h-full flex items-center justify-center overflow-hidden">
                        
                        {/* Media Title (Left Top) */}
                        {currentMedia.title && (
                          <div className="absolute top-3 left-3 bg-black/75 backdrop-blur-md text-white font-bold text-[11px] md:text-xs py-1.5 px-3 rounded-xl shadow-lg border border-white/10 z-30 flex items-center gap-1.5 select-none pointer-events-none">
                            <span className="w-1.5 h-1.5 rounded-full bg-yellow-400 animate-pulse"></span>
                            <span className="truncate max-w-[150px] md:max-w-[220px]">{currentMedia.title}</span>
                          </div>
                        )}

                        {/* Zoom Button (Right Top) */}
                        <button 
                          onClick={() => setZoomMedia({ type: currentMedia.type, url: currentMedia.url, title: currentMedia.title })}
                          className="absolute top-3 right-3 bg-black/60 hover:bg-black/80 text-white p-1.5 rounded-lg hover:scale-105 active:scale-95 transition z-30 backdrop-blur-md border border-white/10 shadow-lg flex items-center justify-center"
                          title="크게 보기"
                        >
                          <Maximize2 className="w-3.5 h-3.5 text-yellow-300" />
                        </button>

                        {currentMedia.type === 'video' ? (
                          <div className="w-full h-full bg-black flex flex-col justify-between relative group/video">
                            {getEmbedUrl(currentMedia.url, useSafePlayer) ? (
                              <div className="relative flex-1 w-full bg-black">
                                <iframe 
                                  className="w-full h-full absolute inset-0" 
                                  src={getEmbedUrl(currentMedia.url, useSafePlayer)!} 
                                  title="YouTube video player" 
                                  frameBorder="0" 
                                  allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture" 
                                  referrerPolicy="strict-origin-when-cross-origin"
                                  allowFullScreen
                                ></iframe>
                              </div>
                            ) : (
                              <div className="w-full h-full flex flex-col items-center justify-center text-gray-400 bg-gray-900 p-4 text-center">
                                <Video className="w-10 h-10 mb-2 text-yellow-400 animate-pulse" />
                                <p className="text-xs font-bold text-gray-300">올바르지 않은 유튜브 주소</p>
                                <p className="text-[10px] text-gray-500 mt-1 max-w-[240px] truncate">{currentMedia.url}</p>
                              </div>
                            )}

                            {/* Control Overlay Bar / Support Panel for Sandbox bypass */}
                            {getYoutubeVideoId(currentMedia.url) && (
                              <div className="bg-gray-950 border-t border-gray-800/80 p-2 flex flex-wrap items-center justify-between gap-1.5 z-20">
                                <div className="flex items-center gap-1.5">
                                  <button
                                    onClick={() => setUseSafePlayer(!useSafePlayer)}
                                    className={`text-[10px] md:text-xs py-1 px-2 rounded-lg font-black transition-all flex items-center gap-1 ${
                                      !useSafePlayer 
                                        ? 'bg-emerald-500/20 text-emerald-400 border border-emerald-500/30' 
                                        : 'bg-blue-500/20 text-blue-400 border border-blue-500/30'
                                    }`}
                                    title={!useSafePlayer ? "쿠키 제한 모드로 전환합니다" : "기본 플레이어 모드로 전환합니다"}
                                  >
                                    <span className={`w-1.5 h-1.5 rounded-full ${!useSafePlayer ? 'bg-emerald-400 animate-pulse' : 'bg-blue-400 animate-pulse'}`}></span>
                                    {!useSafePlayer ? "기본 플레이어 (권장)" : "쿠키 제한 모드"}
                                  </button>
                                </div>
                                <a 
                                  href={`https://www.youtube.com/watch?v=${getYoutubeVideoId(currentMedia.url)}`}
                                  target="_blank" 
                                  rel="noopener noreferrer"
                                  className="text-[10px] md:text-xs bg-red-600/95 hover:bg-red-700 text-white font-bold py-1 px-2.5 rounded-lg flex items-center gap-1 transition shadow-md active:scale-95"
                                >
                                  <ExternalLink className="w-3 h-3" />
                                  <span>유튜브에서 직접 보기</span>
                                </a>
                              </div>
                            )}
                          </div>
                        ) : (
                          <img 
                            src={currentMedia.url} 
                            alt={currentMedia.title || post.title} 
                            className="max-w-full max-h-full w-auto h-auto object-contain cursor-pointer transition-transform duration-700 hover:scale-105 select-none" 
                            onClick={() => setZoomMedia({ type: 'image', url: currentMedia.url, title: currentMedia.title })}
                            onError={(e) => {
                              (e.target as HTMLImageElement).src = 'https://images.unsplash.com/photo-1618005182384-a83a8bd57fbe?auto=format&fit=crop&w=800&q=80';
                            }}
                          />
                        )}

                        {/* Slider Navigation */}
                        {mediaList.length > 1 && (
                          <>
                            <button 
                              onClick={() => handleMediaPrev(post.id, mediaList.length)}
                              className="absolute left-3 top-1/2 -translate-y-1/2 p-2 bg-black/60 text-white rounded-full hover:bg-black/80 active:scale-90 transition z-20 backdrop-blur-sm border border-white/10"
                            >
                              <ChevronLeft className="w-4 h-4" />
                            </button>
                            <button 
                              onClick={() => handleMediaNext(post.id, mediaList.length)}
                              className="absolute right-3 top-1/2 -translate-y-1/2 p-2 bg-black/60 text-white rounded-full hover:bg-black/80 active:scale-90 transition z-20 backdrop-blur-sm border border-white/10"
                            >
                              <ChevronRight className="w-4 h-4" />
                            </button>

                            {/* Dot Indicators */}
                            <div className="absolute bottom-3 left-1/2 -translate-x-1/2 flex items-center gap-1.5 bg-black/60 px-3 py-1.5 rounded-full backdrop-blur-md border border-white/10 z-20">
                              {mediaList.map((_, mIdx) => (
                                <button 
                                  key={mIdx}
                                  onClick={() => setActiveMediaIndexes(prev => ({ ...prev, [post.id]: mIdx }))}
                                  className={`w-1.5 h-1.5 rounded-full transition-all duration-300 ${mIdx === activeIndex ? 'bg-yellow-400 w-3' : 'bg-white/50'}`}
                                />
                              ))}
                            </div>

                            {/* Counter Badge */}
                            <div className="absolute top-3 right-12 bg-black/60 text-[10px] font-black text-yellow-300 px-2.5 py-1 rounded-md backdrop-blur-sm border border-white/10 z-20">
                              {activeIndex + 1} / {mediaList.length}
                            </div>
                          </>
                        )}
                      </div>
                    )}
                  </div>

                  {/* Content Section */}
                  <div className="w-full md:w-1/2 flex flex-col justify-between">
                    <div>
                      <div className="flex flex-wrap items-center gap-2 mb-4">
                        <span className="inline-block px-3 py-1 bg-yellow-100 text-yellow-800 rounded-full text-xs font-black uppercase">
                          NEWS
                        </span>
                        {post.priority && post.priority > 0 ? (
                          <span className="inline-block px-3 py-1 bg-rose-600 text-white rounded-full text-xs font-black flex items-center gap-1 shadow-sm">
                            ★ {post.priority}순위 노출
                          </span>
                        ) : null}
                      </div>
                      <h2 className="text-2xl md:text-3xl font-black text-gray-900 mb-4 leading-tight whitespace-pre-wrap">{post.title}</h2>
                      {/* Rich Text Render Area */}
                      <div 
                        className="text-gray-600 leading-relaxed whitespace-pre-wrap text-sm md:text-base prose prose-slate max-w-none"
                        dangerouslySetInnerHTML={{ __html: post.content }}
                      />
                    </div>
                    
                    <div className="mt-8 pt-6 border-t border-gray-100 flex items-center justify-between">
                      <span className="text-sm font-bold text-gray-400">
                        {new Date(post.createdAt).toLocaleDateString('ko-KR')}
                      </span>
                      
                      {isAdmin && (
                        <div className="flex gap-2">
                          <button onClick={() => openModal(post)} className="text-sm font-bold text-indigo-600 hover:text-indigo-800 bg-indigo-50 px-3 py-1.5 rounded-lg transition">수정</button>
                          <button onClick={() => deletePost(post.id)} className="text-sm font-bold text-red-600 hover:text-red-800 bg-red-50 px-3 py-1.5 rounded-lg transition">삭제</button>
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              );
            })
          )}
        </div>
      </div>

      {/* Admin Advanced Write/Edit Modal */}
      {isModalOpen && isAdmin && (
        <div className="fixed inset-0 bg-black/70 backdrop-blur-sm z-[200] flex items-center justify-center p-4">
          <div className="bg-white rounded-3xl w-full max-w-3xl p-6 md:p-8 shadow-2xl relative overflow-y-auto max-h-[92vh] border border-gray-100">
            <button 
              onClick={() => setIsModalOpen(false)} 
              className="absolute top-6 right-6 p-2 bg-gray-100 rounded-full hover:bg-gray-200 transition z-10"
              title="닫기"
            >
              <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={2.5} stroke="currentColor" className="w-5 h-5 text-gray-600">
                <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
            
            <div className="flex items-center gap-2.5 mb-6 border-b border-gray-100 pb-4">
              <Sparkles className="w-6 h-6 text-yellow-500 fill-yellow-400 animate-pulse" />
              <h2 className="text-2xl font-black text-gray-900">{editingPost ? '고급 게시물 수정' : '고급 새 게시물 작성'}</h2>
            </div>
            
            <div className="flex flex-col gap-6">
              {/* Title & Priority Grid */}
              <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                <div className="md:col-span-2">
                  <label className="block text-sm font-bold text-gray-700 mb-1.5">제목</label>
                  <input 
                    type="text" 
                    value={title} 
                    onChange={e => setTitle(e.target.value)} 
                    className="w-full px-4 py-3 rounded-xl border border-gray-200 focus:outline-none focus:ring-2 focus:ring-indigo-500 bg-gray-50 text-gray-900 font-bold text-lg" 
                    placeholder="제목을 입력하세요" 
                  />
                </div>
                <div>
                  <label className="block text-sm font-bold text-gray-700 mb-1.5">노출 순위 설정</label>
                  <div className="flex rounded-xl bg-gray-100 p-1 border border-gray-200 h-[52px] items-center">
                    {[0, 1, 2, 3].map((val) => (
                      <button
                        key={val}
                        type="button"
                        onClick={() => setPriority(val)}
                        className={`flex-1 h-full rounded-lg font-black text-xs transition-all ${
                          priority === val 
                            ? 'bg-indigo-600 text-white shadow-md' 
                            : 'text-gray-500 hover:text-gray-900 hover:bg-gray-200/50'
                        }`}
                      >
                        {val === 0 ? '일반' : `${val}순위`}
                      </button>
                    ))}
                  </div>
                </div>
              </div>
              
              {/* Rich Editor Panel */}
              <div>
                <div className="flex justify-between items-center mb-1.5">
                  <label className="block text-sm font-bold text-gray-700">본문 내용 (전문 에디터)</label>
                  <div className="flex rounded-lg bg-gray-100 p-1 border border-gray-200 text-xs">
                    <button 
                      type="button"
                      onClick={() => setIsPreviewMode(false)}
                      className={`px-3 py-1 rounded-md font-bold transition flex items-center gap-1 ${!isPreviewMode ? 'bg-white text-indigo-600 shadow-sm' : 'text-gray-500 hover:text-gray-900'}`}
                    >
                      <Bold className="w-3.5 h-3.5" /> 에디터 작성
                    </button>
                    <button 
                      type="button"
                      onClick={() => setIsPreviewMode(true)}
                      className={`px-3 py-1 rounded-md font-bold transition flex items-center gap-1 ${isPreviewMode ? 'bg-white text-indigo-600 shadow-sm' : 'text-gray-500 hover:text-gray-900'}`}
                    >
                      <Eye className="w-3.5 h-3.5" /> 실시간 미리보기
                    </button>
                  </div>
                </div>

                {!isPreviewMode ? (
                  <div className="border border-gray-200 rounded-2xl overflow-hidden bg-gray-50 flex flex-col shadow-sm">
                    {/* Professional Rich Formatting Toolbar */}
                    <div className="flex flex-wrap items-center gap-1 p-2 bg-white border-b border-gray-200">
                      
                      {/* Bold / Italic / Underline */}
                      <div className="flex items-center gap-0.5 pr-1.5 border-r border-gray-200">
                        <button 
                          type="button" 
                          onClick={() => applyFormat('<strong>', '</strong>')} 
                          className="p-1.5 text-gray-600 hover:text-black hover:bg-gray-100 rounded transition" 
                          title="굵게 (Bold)"
                        >
                          <Bold className="w-4 h-4" />
                        </button>
                        <button 
                          type="button" 
                          onClick={() => applyFormat('<em>', '</em>')} 
                          className="p-1.5 text-gray-600 hover:text-black hover:bg-gray-100 rounded transition" 
                          title="기울임꼴 (Italic)"
                        >
                          <Italic className="w-4 h-4" />
                        </button>
                        <button 
                          type="button" 
                          onClick={() => applyFormat('<u>', '</u>')} 
                          className="p-1.5 text-gray-600 hover:text-black hover:bg-gray-100 rounded transition" 
                          title="밑줄 (Underline)"
                        >
                          <Underline className="w-4 h-4" />
                        </button>
                      </div>

                      {/* Headings */}
                      <div className="flex items-center gap-0.5 px-1.5 border-r border-gray-200">
                        <button 
                          type="button" 
                          onClick={() => applyFormat('<h2 class="text-2xl font-black text-gray-900 border-b pb-1 mt-4 mb-2">', '</h2>')} 
                          className="px-2 py-1 text-[11px] font-black text-gray-600 hover:text-black hover:bg-gray-100 rounded transition" 
                          title="큰 제목 (H1)"
                        >
                          H1
                        </button>
                        <button 
                          type="button" 
                          onClick={() => applyFormat('<h3 class="text-lg font-bold text-gray-800 mt-3 mb-1">', '</h3>')} 
                          className="px-2 py-1 text-[11px] font-black text-gray-600 hover:text-black hover:bg-gray-100 rounded transition" 
                          title="소제목 (H2)"
                        >
                          H2
                        </button>
                      </div>

                      {/* Alignment */}
                      <div className="flex items-center gap-0.5 px-1.5 border-r border-gray-200">
                        <button 
                          type="button" 
                          onClick={() => applyFormat('<div class="text-left">', '</div>')} 
                          className="p-1.5 text-gray-600 hover:text-black hover:bg-gray-100 rounded transition" 
                          title="왼쪽 정렬"
                        >
                          <AlignLeft className="w-4 h-4" />
                        </button>
                        <button 
                          type="button" 
                          onClick={() => applyFormat('<div class="text-center">', '</div>')} 
                          className="p-1.5 text-gray-600 hover:text-black hover:bg-gray-100 rounded transition" 
                          title="가운데 정렬"
                        >
                          <AlignCenter className="w-4 h-4" />
                        </button>
                        <button 
                          type="button" 
                          onClick={() => applyFormat('<div class="text-right">', '</div>')} 
                          className="p-1.5 text-gray-600 hover:text-black hover:bg-gray-100 rounded transition" 
                          title="오른쪽 정렬"
                        >
                          <AlignRight className="w-4 h-4" />
                        </button>
                      </div>

                      {/* Lists */}
                      <div className="flex items-center gap-0.5 px-1.5 border-r border-gray-200">
                        <button 
                          type="button" 
                          onClick={() => applyFormat('<ul class="list-disc pl-5 my-2"><li>', '</li></ul>')} 
                          className="p-1.5 text-gray-600 hover:text-black hover:bg-gray-100 rounded transition" 
                          title="글머리 기호 목록"
                        >
                          <List className="w-4 h-4" />
                        </button>
                        <button 
                          type="button" 
                          onClick={() => applyFormat('<ol class="list-decimal pl-5 my-2"><li>', '</li></ol>')} 
                          className="p-1.5 text-gray-600 hover:text-black hover:bg-gray-100 rounded transition" 
                          title="번호 매기기 목록"
                        >
                          <ListOrdered className="w-4 h-4" />
                        </button>
                        <button 
                          type="button" 
                          onClick={() => applyFormat('<li>', '</li>')} 
                          className="px-2 py-1 text-[10px] font-bold text-gray-500 hover:text-black hover:bg-gray-100 rounded transition" 
                          title="목록 요소 추가 (li)"
                        >
                          +항목
                        </button>
                      </div>

                      {/* Accent Colors */}
                      <div className="flex items-center gap-1 px-1.5 border-r border-gray-200">
                        <button 
                          type="button" 
                          onClick={() => applyFormat('<span class="text-yellow-500 font-bold">', '</span>')} 
                          className="w-4.5 h-4.5 rounded-full bg-yellow-400 border border-gray-300 hover:scale-110 active:scale-90 transition" 
                          title="노란색 포인트"
                        />
                        <button 
                          type="button" 
                          onClick={() => applyFormat('<span class="text-red-500 font-bold">', '</span>')} 
                          className="w-4.5 h-4.5 rounded-full bg-red-500 border border-gray-300 hover:scale-110 active:scale-90 transition" 
                          title="빨간색 포인트"
                        />
                        <button 
                          type="button" 
                          onClick={() => applyFormat('<span class="text-indigo-600 font-bold">', '</span>')} 
                          className="w-4.5 h-4.5 rounded-full bg-indigo-600 border border-gray-300 hover:scale-110 active:scale-90 transition" 
                          title="파란색 포인트"
                        />
                        <button 
                          type="button" 
                          onClick={() => applyFormat('<span class="text-emerald-500 font-bold">', '</span>')} 
                          className="w-4.5 h-4.5 rounded-full bg-emerald-500 border border-gray-300 hover:scale-110 active:scale-90 transition" 
                          title="초록색 포인트"
                        />
                        <button 
                          type="button" 
                          onClick={() => applyFormat('<span class="inline-block px-2.5 py-0.5 rounded-full text-[10px] font-black bg-yellow-100 text-yellow-800 uppercase">', '</span>')} 
                          className="px-1.5 py-0.5 bg-yellow-100 text-yellow-800 rounded font-black text-[9px] transition hover:bg-yellow-200" 
                          title="골든 배지 강조"
                        >
                          배지
                        </button>
                      </div>

                      {/* Special decor & horizontal line */}
                      <div className="flex items-center gap-0.5 px-1.5">
                        <button 
                          type="button" 
                          onClick={() => applyFormat('✨ <b>', '</b> ✨')} 
                          className="p-1.5 text-yellow-600 hover:text-yellow-700 hover:bg-yellow-50 rounded transition" 
                          title="블링블링 효과"
                        >
                          <Sparkles className="w-3.5 h-3.5" />
                        </button>
                        <button 
                          type="button" 
                          onClick={() => applyFormat('<blockquote class="border-l-4 border-yellow-400 pl-3 py-1 bg-yellow-50/40 italic text-gray-600 my-2 rounded-r">', '</blockquote>')} 
                          className="p-1.5 text-gray-500 hover:text-black hover:bg-gray-100 rounded transition" 
                          title="인용 박스"
                        >
                          <Quote className="w-3.5 h-3.5" />
                        </button>
                        <button 
                          type="button" 
                          onClick={() => {
                            const el = textareaRef.current;
                            if (!el) return;
                            const start = el.selectionStart;
                            const text = el.value;
                            const divLine = '<hr class="my-4 border-t border-gray-200" />';
                            const newVal = text.substring(0, start) + divLine + text.substring(start);
                            setContent(newVal);
                            setTimeout(() => el.focus(), 0);
                          }} 
                          className="px-1.5 py-0.5 text-[10px] font-bold text-gray-500 hover:text-black hover:bg-gray-100 rounded transition" 
                          title="가로선 구분선 추가"
                        >
                          구분선
                        </button>
                      </div>

                    </div>
                    {/* Content textarea */}
                    <textarea 
                      ref={textareaRef}
                      value={content} 
                      onChange={e => setContent(e.target.value)} 
                      rows={8} 
                      className="w-full px-4 py-3 focus:outline-none bg-white resize-y text-sm leading-relaxed font-mono text-gray-800" 
                      placeholder="이곳에 내용을 적으세요. 위 에디터 툴바 버튼을 사용하여 글을 자유롭고 프로페셔널하게 꾸며보세요!" 
                    />
                  </div>
                ) : (
                  // Live Preview rendering area
                  <div className="border border-gray-200 rounded-2xl p-5 bg-white min-h-[190px] max-h-[400px] overflow-y-auto shadow-inner">
                    {content.trim() ? (
                      <div 
                        className="text-gray-600 leading-relaxed whitespace-pre-wrap text-sm md:text-base prose prose-slate max-w-none rich-text-preview"
                        dangerouslySetInnerHTML={{ __html: content }}
                      />
                    ) : (
                      <p className="text-gray-400 text-sm italic text-center py-10">실시간 미리보기 화면입니다. 본문을 입력해주세요.</p>
                    )}
                  </div>
                )}
              </div>

              {/* Dynamic Multiple Image URLs Section */}
              <div className="border-t border-gray-100 pt-5">
                <div className="flex justify-between items-center mb-2">
                  <label className="block text-sm font-bold text-gray-700 flex items-center gap-1.5">
                    <Image className="w-4 h-4 text-indigo-500" />
                    이미지 업로드 목록 <span className="text-xs font-normal text-gray-400">(여러 장 등록 시 슬라이더로 자동 작동합니다)</span>
                  </label>
                  <button 
                    type="button" 
                    onClick={handleAddImageUrlField}
                    className="px-3 py-1 bg-indigo-50 text-indigo-600 text-xs font-black rounded-lg hover:bg-indigo-100 transition flex items-center gap-1"
                  >
                    <Plus className="w-3.5 h-3.5" /> 이미지 추가
                  </button>
                </div>
                
                <div className="flex flex-col gap-2.5">
                  {imageUrls.map((url, index) => (
                    <div key={index} className="flex flex-col gap-1.5 bg-gray-50 p-2.5 rounded-xl border border-gray-100">
                      <div className="flex items-center gap-2">
                        <span className="text-xs font-black text-gray-400 w-6 text-center">{index + 1}</span>
                        <input 
                          type="text" 
                          value={url} 
                          onChange={e => handleImageUrlChange(index, e.target.value)} 
                          className="flex-grow px-3 py-2 rounded-lg border border-gray-200 text-xs bg-white focus:outline-none focus:ring-1 focus:ring-indigo-500 text-gray-800" 
                          placeholder="https://example.com/image.png" 
                        />
                        <button 
                          type="button" 
                          onClick={() => handleRemoveImageUrlField(index)}
                          className="p-2 text-red-500 hover:text-red-700 hover:bg-red-50 rounded-lg transition"
                          title="삭제"
                        >
                          <Trash2 className="w-4 h-4" />
                        </button>
                      </div>
                      
                      {/* Image Title / Caption Input */}
                      <div className="pl-8 flex items-center gap-2">
                        <span className="text-[10px] font-bold text-gray-500 select-none">이미지 제목:</span>
                        <input 
                          type="text" 
                          value={imageTitles[index] || ''} 
                          onChange={e => handleImageTitleChange(index, e.target.value)} 
                          className="flex-grow px-3 py-1.5 rounded-lg border border-gray-200 text-xs bg-white focus:outline-none focus:ring-1 focus:ring-indigo-500 text-gray-800" 
                          placeholder="미디어 좌측 상단에 노출될 개별 제목을 입력해 주세요 (선택사항)" 
                        />
                      </div>
                      
                      {/* Instant Thumbnail Preview for easy verification */}
                      {url.trim() && (
                        <div className="pl-8 flex items-center gap-2">
                          <img 
                            src={url} 
                            alt="preview" 
                            className="w-12 h-12 object-cover rounded-lg border border-gray-200 bg-white" 
                            onError={(e) => { (e.target as HTMLImageElement).style.display = 'none'; }}
                          />
                          <span className="text-[10px] text-emerald-600 font-bold flex items-center gap-0.5">
                            <Check className="w-3 h-3" /> 라이브 미리보기 연결 성공
                          </span>
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              </div>

              {/* Dynamic Multiple Video URLs Section */}
              <div className="border-t border-gray-100 pt-5">
                <div className="flex justify-between items-center mb-2">
                  <label className="block text-sm font-bold text-gray-700 flex items-center gap-1.5">
                    <Video className="w-4 h-4 text-rose-500" />
                    유튜브 동영상 업로드 목록 <span className="text-xs font-normal text-gray-400">(여러 개 등록 시 이미지와 함께 슬라이더에 추가됩니다)</span>
                  </label>
                  <button 
                    type="button" 
                    onClick={handleAddVideoUrlField}
                    className="px-3 py-1 bg-indigo-50 text-indigo-600 text-xs font-black rounded-lg hover:bg-indigo-100 transition flex items-center gap-1"
                  >
                    <Plus className="w-3.5 h-3.5" /> 동영상 추가
                  </button>
                </div>

                <div className="flex flex-col gap-2.5">
                  {videoUrls.map((url, index) => (
                    <div key={index} className="flex flex-col gap-1.5 bg-gray-50 p-2.5 rounded-xl border border-gray-100">
                      <div className="flex items-center gap-2">
                        <span className="text-xs font-black text-gray-400 w-6 text-center">{index + 1}</span>
                        <input 
                          type="text" 
                          value={url} 
                          onChange={e => handleVideoUrlChange(index, e.target.value)} 
                          className="flex-grow px-3 py-2 rounded-lg border border-gray-200 text-xs bg-white focus:outline-none focus:ring-1 focus:ring-indigo-500 text-gray-800" 
                          placeholder="유튜브 링크 주소 입력 (예: https://www.youtube.com/watch?v=...)" 
                        />
                        <button 
                          type="button" 
                          onClick={() => handleRemoveVideoUrlField(index)}
                          className="p-2 text-red-500 hover:text-red-700 hover:bg-red-50 rounded-lg transition"
                          title="삭제"
                        >
                          <Trash2 className="w-4 h-4" />
                        </button>
                      </div>

                      {/* Video Title / Caption Input */}
                      <div className="pl-8 flex items-center gap-2">
                        <span className="text-[10px] font-bold text-gray-500 select-none">동영상 제목:</span>
                        <input 
                          type="text" 
                          value={videoTitles[index] || ''} 
                          onChange={e => handleVideoTitleChange(index, e.target.value)} 
                          className="flex-grow px-3 py-1.5 rounded-lg border border-gray-200 text-xs bg-white focus:outline-none focus:ring-1 focus:ring-indigo-500 text-gray-800" 
                          placeholder="미디어 좌측 상단에 노출될 개별 제목을 입력해 주세요 (선택사항)" 
                        />
                      </div>

                      {/* YouTube Thumbnail Preview */}
                      {url.trim() && getEmbedUrl(url) && (
                        <div className="pl-8 flex items-center gap-2">
                          <img 
                            src={`https://img.youtube.com/vi/${url.match(/^.*(youtu.be\/|v\/|u\/\w\/|embed\/|watch\?v=|&v=)([^#&?]*).*/)?.[2]}/hqdefault.jpg`} 
                            alt="video preview" 
                            className="w-16 h-10 object-cover rounded-lg border border-gray-200 bg-white"
                            onError={(e) => { (e.target as HTMLImageElement).style.display = 'none'; }}
                          />
                          <span className="text-[10px] text-amber-600 font-bold flex items-center gap-0.5">
                            <Check className="w-3 h-3" /> 유튜브 영상 감지 완료
                          </span>
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              </div>
            </div>

            <div className="mt-8 flex justify-end gap-3 border-t border-gray-100 pt-5">
              <button onClick={() => setIsModalOpen(false)} className="px-6 py-3 rounded-xl bg-gray-100 text-gray-700 font-bold hover:bg-gray-200 transition">취소</button>
              <button onClick={savePost} className="px-8 py-3 rounded-xl bg-indigo-600 text-white font-bold hover:bg-indigo-700 transition shadow-lg hover:scale-102 active:scale-95">저장하기</button>
            </div>
          </div>
        </div>
      )}

      {/* Custom Popup Modal for Actual Size Media Zooming */}
      {zoomMedia && (
        <div 
          className="fixed inset-0 bg-black/90 backdrop-blur-md z-[300] flex flex-col items-center justify-center p-4 md:p-8"
          onClick={() => setZoomMedia(null)}
        >
          {/* Close Button */}
          <button 
            onClick={() => setZoomMedia(null)}
            className="absolute top-6 right-6 p-3 bg-white/10 hover:bg-white/20 text-white rounded-full transition shadow-lg hover:scale-110 active:scale-90 z-50 border border-white/10"
            title="닫기"
          >
            <X className="w-6 h-6" />
          </button>

          {/* Title/Caption */}
          {zoomMedia.title && (
            <div 
              className="mb-4 bg-black/50 text-yellow-300 text-sm md:text-base font-black px-4 py-2 rounded-xl backdrop-blur-sm border border-yellow-400/20 max-w-full text-center shadow-md animate-bounce"
              onClick={(e) => e.stopPropagation()}
            >
              ✨ {zoomMedia.title}
            </div>
          )}

          {/* Media Container */}
          <div 
            className="relative flex items-center justify-center max-w-[90vw] max-h-[80vh]"
            onClick={(e) => e.stopPropagation()}
          >
            {zoomMedia.type === 'video' ? (
              <div className="w-[85vw] max-w-4xl aspect-video bg-black rounded-2xl overflow-hidden shadow-2xl border border-white/10 flex flex-col">
                {getEmbedUrl(zoomMedia.url, useSafePlayer) ? (
                  <div className="relative flex-1 w-full bg-black">
                    <iframe 
                      className="w-full h-full absolute inset-0" 
                      src={getEmbedUrl(zoomMedia.url, useSafePlayer)!} 
                      title="YouTube video player full view" 
                      frameBorder="0" 
                      allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture" 
                      referrerPolicy="strict-origin-when-cross-origin"
                      allowFullScreen
                    ></iframe>
                  </div>
                ) : (
                  <div className="w-full h-full flex flex-col items-center justify-center text-gray-400 bg-gray-900 p-4 text-center">
                    <Video className="w-12 h-12 mb-2 text-yellow-400 animate-pulse" />
                    <p className="text-xs font-bold text-gray-300">올바르지 않은 유튜브 주소</p>
                  </div>
                )}

                {/* Full screen modal controls */}
                {getYoutubeVideoId(zoomMedia.url) && (
                  <div className="bg-gray-950 border-t border-gray-800 p-3 flex items-center justify-between gap-2">
                    <div className="flex items-center gap-2">
                      <button
                        onClick={() => setUseSafePlayer(!useSafePlayer)}
                        className={`text-xs py-1.5 px-3 rounded-lg font-black transition-all flex items-center gap-1.5 ${
                          !useSafePlayer 
                            ? 'bg-emerald-500/20 text-emerald-400 border border-emerald-500/30' 
                            : 'bg-blue-500/20 text-blue-400 border border-blue-500/30'
                        }`}
                        title={!useSafePlayer ? "쿠키 제한 모드로 전환합니다" : "기본 플레이어 모드로 전환합니다"}
                      >
                        <span className={`w-1.5 h-1.5 rounded-full ${!useSafePlayer ? 'bg-emerald-400 animate-pulse' : 'bg-blue-400 animate-pulse'}`}></span>
                        {!useSafePlayer ? "기본 플레이어 (권장)" : "쿠키 제한 모드"}
                      </button>
                    </div>
                    <a 
                      href={`https://www.youtube.com/watch?v=${getYoutubeVideoId(zoomMedia.url)}`}
                      target="_blank" 
                      rel="noopener noreferrer"
                      className="text-xs bg-red-600/95 hover:bg-red-700 text-white font-black py-1.5 px-4 rounded-lg flex items-center gap-1.5 transition shadow-lg active:scale-95"
                    >
                      <ExternalLink className="w-3.5 h-3.5" />
                      <span>유튜브에서 직접 보기</span>
                    </a>
                  </div>
                )}
              </div>
            ) : (
              <img 
                src={zoomMedia.url} 
                alt={zoomMedia.title || "Zoomed"} 
                className="max-w-full max-h-[80vh] object-contain rounded-2xl shadow-2xl border border-white/5 select-none" 
              />
            )}
          </div>
          
          <p className="mt-4 text-[11px] text-gray-400 select-none">바깥 영역을 클릭하거나 우측 상단 닫기 버튼을 누르면 돌아갑니다.</p>
        </div>
      )}
    </div>
  );
};

export default CompetitionsPage;
